import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    PATH_GRID_SIZE,
    createPathMemoryRound,
    GameShell,
} from '../shared';
import { formatGameCopy } from './gameCopy';

function MemoryMatrixGame({ copy = {}, initialLevel = 1, ...props }) {
    const startLevel = Math.max(1, Math.min(18, Math.round(Number(initialLevel) || 1)));
    const stageTimerRef = useRef(null);
    const [level, setLevel] = useState(startLevel);
    const [round, setRound] = useState(() => createPathMemoryRound(startLevel));
    const [stage, setStage] = useState('countdown');
    const [countdown, setCountdown] = useState(3);
    const [selected, setSelected] = useState([]);
    const [failedCell, setFailedCell] = useState(null);
    const [streak, setStreak] = useState(0);
    const pathSet = useMemo(() => new Set(round.path), [round.path]);
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const clearStageTimer = useCallback(() => {
        if (stageTimerRef.current) {
            window.clearTimeout(stageTimerRef.current);
            stageTimerRef.current = null;
        }
    }, []);

    const resetRound = useCallback((nextLevel = level) => {
        clearStageTimer();
        setRound(createPathMemoryRound(nextLevel));
        setSelected([]);
        setFailedCell(null);
        setCountdown(3);
        setStage('countdown');
    }, [clearStageTimer, level]);

    useEffect(() => {
        clearStageTimer();

        if (stage === 'countdown') {
            stageTimerRef.current = window.setTimeout(() => {
                stageTimerRef.current = null;

                if (countdown > 1) {
                    setCountdown((value) => value - 1);
                    return;
                }

                setCountdown(0);
                setStage('preview');
            }, 700);
        }

        if (stage === 'preview') {
            stageTimerRef.current = window.setTimeout(() => {
                stageTimerRef.current = null;
                setStage('input');
            }, 3000);
        }

        if (stage === 'fail') {
            stageTimerRef.current = window.setTimeout(() => resetRound(level), 640);
        }

        if (stage === 'success') {
            stageTimerRef.current = window.setTimeout(() => resetRound(level), 820);
        }

        return clearStageTimer;
    }, [clearStageTimer, countdown, level, resetRound, stage]);

    const chooseCell = (cellIndex) => {
        if (stage !== 'input') {
            return;
        }

        if (selectedSet.has(cellIndex)) {
            return;
        }

        if (!pathSet.has(cellIndex)) {
            setFailedCell(cellIndex);
            setStreak(0);
            setStage('fail');
            return;
        }

        const nextSelected = [...selected, cellIndex];
        setSelected(nextSelected);
        setStreak((value) => value + 1);

        if (nextSelected.length === round.path.length) {
            const nextLevel = Math.min(18, level + 1);
            setLevel(nextLevel);
            setStage('success');
        }
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level, streak }}
            tone={stage === 'success' ? 'good' : stage === 'fail' ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__path-game">
                <div
                    aria-label={formatGameCopy(copy.streak, { count: streak })}
                    className="UFOV-brain-games__path-streak"
                >
                    {streak}
                </div>
                <div className="UFOV-brain-games__path-grid-wrap">
                    {stage === 'countdown' ? (
                        <div className="UFOV-brain-games__path-countdown" aria-live="assertive">{countdown}</div>
                    ) : null}
                    <div className={`UFOV-brain-games__path-grid is-${stage}`} role="grid" aria-label={copy.gridMemoryBoard}>
                        {Array.from({ length: PATH_GRID_SIZE }, (_, index) => {
                            const inPath = pathSet.has(index);
                            const isSelected = selectedSet.has(index);
                            const isFailed = failedCell === index;
                            return (
                                <button
                                    aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                    className={`UFOV-brain-games__path-cell ${isSelected ? 'is-picked' : ''} ${isFailed ? 'is-failed' : ''}`.trim()}
                                    disabled={stage !== 'input'}
                                    key={index}
                                    onClick={() => chooseCell(index)}
                                    type="button"
                                >
                                    <span className="UFOV-brain-games__path-cell-inner" aria-hidden="true">
                                        <span className={`UFOV-brain-games__path-cell-face is-front${inPath ? ' is-path' : ''}`} />
                                        <span className="UFOV-brain-games__path-cell-face is-back" />
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </GameShell>
    );
}

export default MemoryMatrixGame;
